import moment from 'moment';

import type {
  BulkOfferModalSelectedTimelines,
  BulkOfferModalTimelineData
} from 'modules/subcampaigns/types/BulkOfferModal.type';

function isValidStartDates(timeFrameStartDates: Array<string | moment.Moment>): boolean {
  if (timeFrameStartDates.length > 1) {
    let isOrdered = true;
    for (let i = 0; i < timeFrameStartDates.length - 1; i++) {
      isOrdered = isOrdered && moment(timeFrameStartDates[i]).isSameOrBefore(timeFrameStartDates[i + 1]);
    }

    if (!isOrdered) {
      return false;
    }
  }

  return true;
}

export function isValidBulkChangeTimelineOrder(
  pickedTimelines: BulkOfferModalSelectedTimelines,
  contentTimelines: BulkOfferModalTimelineData
): boolean {
  const timeFrameStartDates: Array<string> = [];
  const orderedTimelines: Array<keyof BulkOfferModalSelectedTimelines> = [
    'contentApproval',
    'goLive',
    'insightsProvision'
  ];

  orderedTimelines.forEach((key) => {
    if (pickedTimelines[key]) {
      timeFrameStartDates.push(contentTimelines[key].startDate);
    }
  });

  return isValidStartDates(timeFrameStartDates);
}

type GroupedDeliverablesShape = {
  deliverables: Array<{
    id: number,
    contentApprovalTimeline?: { startDate: string },
    contentGoLiveTimeline?: { startDate: string },
    insightsProvisionTimeline?: { startDate: string }
  }>,
  creatorProfile: {
    name: string
  }
};

export function getCreatorNameWithTimelineOverlapForPostpone(
  groupedDeliverables: Array<GroupedDeliverablesShape>,
  selectedDeliverableIds: Array<number>,
  pickedTimelines: BulkOfferModalSelectedTimelines,
  days: number
): string | undefined {
  const orderedTimelinePairs: Array<[string, string]> = [
    ['contentApproval', 'contentApprovalTimeline'],
    ['goLive', 'contentGoLiveTimeline'],
    ['insightsProvision', 'insightsProvisionTimeline']
  ];

  if (selectedDeliverableIds.length === 0 || Object.values(pickedTimelines).every(isPicked => !isPicked)) {
    return undefined;
  }

  const creatorWithInvalidTimeline = groupedDeliverables.find(offer => {
    const availableDeliverables = offer.deliverables
      .filter(deliverable => selectedDeliverableIds.includes(deliverable.id));

    return availableDeliverables.some(deliverable => {
      const mappedStartDates: Array<moment.Moment> = [];
      orderedTimelinePairs.forEach(([timelineKey, deliverableTimelineField]) => {
        if (deliverable[deliverableTimelineField]?.startDate) {
          const daysToAdd = (pickedTimelines[timelineKey])
            ? days
            : 0;

          mappedStartDates.push(moment(deliverable[deliverableTimelineField].startDate).add(daysToAdd, 'days'));
        }
      });

      return !isValidStartDates(mappedStartDates);
    });
  });

  return creatorWithInvalidTimeline?.creatorProfile?.name;
}

export function getCreatorNameWithTimelineOverlapForChange(
  groupedDeliverables: Array<GroupedDeliverablesShape>,
  selectedDeliverableIds: Array<number>,
  pickedTimelines: BulkOfferModalSelectedTimelines,
  contentTimelines: BulkOfferModalTimelineData
): string | undefined {
  const orderedTimelinePairs: Array<[string, string]> = [
    ['contentApproval', 'contentApprovalTimeline'],
    ['goLive', 'contentGoLiveTimeline'],
    ['insightsProvision', 'insightsProvisionTimeline']
  ];

  if (selectedDeliverableIds.length === 0 || Object.values(pickedTimelines).every(isPicked => !isPicked)) {
    return undefined;
  }

  const creatorWithInvalidTimeline = groupedDeliverables.find(offer => {
    const availableDeliverables = offer.deliverables
      .filter(deliverable => selectedDeliverableIds.includes(deliverable.id));

    return availableDeliverables.some(deliverable => {
      const mappedStartDates: Array<moment.Moment> = [];
      orderedTimelinePairs.forEach(([timelineKey, deliverableTimelineField]) => {
        if (pickedTimelines[timelineKey]) {
          mappedStartDates.push(contentTimelines[timelineKey].startDate);
        } else if (deliverable[deliverableTimelineField]?.startDate) {
          mappedStartDates.push(deliverable[deliverableTimelineField].startDate);
        }
      });

      return !isValidStartDates(mappedStartDates);
    });
  });

  return creatorWithInvalidTimeline?.creatorProfile?.name;
}
